import React, { useState } from 'react';
import { Box, Popover } from '@mui/material';
import { formatCurrency } from '../../utils/formatUtils';

interface Calculation {
  name: string;
  value: number;
  operation?: 'add' | 'subtract';
}

type DisplayVariant = 'default' | 'netOnly';

interface CalculatedValueDisplayProps {
  calculations?: Calculation[];
  formatFunction?: (value: number) => string;
  isNA?: boolean;
  style?: React.CSSProperties;
  showDetails?: boolean;
  variant?: DisplayVariant;
}

const CalculatedValueDisplay: React.FC<CalculatedValueDisplayProps> = ({
  style,
  calculations,
  formatFunction = formatCurrency,
  isNA = false,
  showDetails = true,
  variant = 'default',
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
  };

  // If isNA is true, show "-"
  if (isNA) {
    return <span style={{ color: '#666', display: 'block', textAlign: 'right' }}>-</span>;
  }

  // If no calculations provided, return empty
  if (!calculations || calculations.length === 0) {
    return <span style={{ display: 'block', textAlign: 'right' }}>-</span>;
  }

  // If only one calculation element, show just that value without popover
  if (calculations.length === 1) {
    const singleValue = calculations[0]?.value || 0;
    return <span style={{ display: 'block', textAlign: 'right' }}>{formatFunction(singleValue)}</span>;
  }

  // Calculate the total
  const total = calculations.reduce((sum, item) => {
    const value = Number(item.value) || 0;
    return item.operation === 'subtract' ? sum - value : sum + value;
  }, 0);

  const firstValue = calculations[0]?.value || 0;

  if (variant === 'netOnly') {
    if (total === 0) {
      return (
        <span
          style={{
            display: 'block',
            textAlign: 'right',
            color: '#1b8a5a',
            fontWeight: 600,
          }}
        >
          ✓
        </span>
      );
    }

    const value = Math.abs(total);
    const prefix = total > 0 ? '+' : '-';
    return (
      <span
        style={{
          display: 'block',
          textAlign: 'right',
          color: total < 0 ? '#c62828' : '#1b5e20',
          fontWeight: 600,
        }}
      >
        {`${prefix}${formatFunction(value)}`}
      </span>
    );
  }

  // If total is 0, just show the first value
  if (total === 0) {
    return <span style={{ display: 'block', textAlign: 'right' }}>{formatFunction(firstValue)}</span>;
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          cursor: showDetails ? 'pointer' : 'default',
          textAlign: 'right',
          width: '100%',
          ...style
        }}
        onMouseEnter={showDetails ? handleMouseEnter : undefined}
        onMouseLeave={showDetails ? handleMouseLeave : undefined}
      >
        <span style={{ color: '#23243A', width: '100%' }}>{formatFunction(firstValue)}</span>
        <span style={{ color: total < 0 ? 'red' : total > 0 ? 'blue' : '#23243A', width: '100%' }}>
          {total !== 0
            ? `${total > 0 ? '+' : '-'}${formatFunction(Math.abs(total))}`
            : ''}
        </span>
      </div>

      {showDetails && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          onClose={handleMouseLeave}
          disableRestoreFocus
          sx={{
            pointerEvents: 'none',
          }}
        >
          <Box sx={{ p: 2, minWidth: '250px', backgroundColor: '#f5f5f5' }}>
            <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
              {calculations.map((item, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: index < calculations.length - 1 ? '4px' : '8px',
                  borderBottom: index < calculations.length - 1 ? '1px solid #ddd' : 'none',
                  paddingBottom: index < calculations.length - 1 ? '4px' : '0'
                }}>
                  <span style={{ color: '#666' }}>
                    {index === 0 ? (
                      item.name
                    ) : (
                      <span style={{ fontSize: '0.85em' }}>
                        {item.operation === 'subtract' ? 'less' : 'add'} {item.name}
                      </span>
                    )}
                  </span>
                  <span style={{ fontWeight: 'bold' }}>
                    {index === 0 ? '' : (item.operation === 'subtract' ? '-' : '+')} {formatFunction(item.value)}
                  </span>
                </div>
              ))}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                borderTop: '2px solid #333',
                paddingTop: '8px',
                marginTop: '8px',
                fontWeight: 'bold'
              }}>
                <span>Discrepancy</span>
                <span style={{ color: total >= 0 ? 'green' : 'red' }}>
                  {total >= 0 ? '+' : '-'}{formatFunction(Math.abs(total))}
                </span>
              </div>
              <div style={{
                fontSize: '0.75em',
                color: '#666',
                marginTop: '4px'
              }}>
                {total >= 0 ?
                  <span style={{ color: 'green' }}>
                    +ve is profit
                  </span>
                  :
                  <span style={{ color: 'red' }}>
                    -ve is loss
                  </span>
                }
              </div>
            </div>
          </Box>
        </Popover>
      )}
    </>
  );
};

export default CalculatedValueDisplay;

